import React, { useRef, useEffect } from 'react';

/*
 * ParticleTerrainCanvas
 * Perspective point-cloud terrain using fractal Brownian motion (fBm) for
 * genuinely random, organic-looking waves. Very subtle opacity.
 */
export default function ParticleTerrainCanvas({ className, style }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    /* ── Value noise building blocks ────────────────────────────────────── */
    function hash(x, z) {
      const n = Math.sin(x * 127.1 + z * 311.7) * 43758.5453;
      return n - Math.floor(n); // [0, 1)
    }

    /* Smooth value noise with quintic interpolation */
    function valueNoise(x, z) {
      const ix = Math.floor(x);
      const iz = Math.floor(z);
      const fx = x - ix;
      const fz = z - iz;
      // Quintic smoothstep: 6t^5 - 15t^4 + 10t^3
      const ux = fx * fx * fx * (fx * (fx * 6 - 15) + 10);
      const uz = fz * fz * fz * (fz * (fz * 6 - 15) + 10);
      const v00 = hash(ix,     iz    );
      const v10 = hash(ix + 1, iz    );
      const v01 = hash(ix,     iz + 1);
      const v11 = hash(ix + 1, iz + 1);
      return v00 + (v10 - v00) * ux + (v01 - v00) * uz + (v00 - v10 - v01 + v11) * ux * uz;
    }

    /*
     * Fractal Brownian Motion — 5 octaves.
     * Low-frequency octave creates broad sweeping waves; higher octaves add
     * random micro-detail. Result is centered around 0.
     */
    function fbm(x, z) {
      let v = 0;
      v += valueNoise(x * 0.38, z * 0.38) * 0.500;  // large swell
      v += valueNoise(x * 0.80, z * 0.80) * 0.250;  // medium roll
      v += valueNoise(x * 1.70, z * 1.70) * 0.125;  // small variation
      v += valueNoise(x * 3.50, z * 3.50) * 0.063;  // fine detail
      v += valueNoise(x * 7.00, z * 7.00) * 0.031;  // micro texture
      return (v - 0.484) * 2.2; // re-centre and scale → roughly [-1, +1]
    }

    function draw() {
      const W = canvas.offsetWidth;
      const H = canvas.offsetHeight;
      if (!W || !H) return;
      canvas.width  = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, W, H);

      /* ── Camera ─────────────────────────────────────────────────────────── */
      const camHeight  = 1.30;
      const camZOffset = 0.50;
      const fovY       = H * 1.10;
      const centerX    = W * 0.44;
      const horizonY   = H * 0.43;

      /* ── Grid ───────────────────────────────────────────────────────────── */
      const NX    = 82;
      const NZ    = 52;
      const terrW = 11;
      const terrD = 8.5;
      const zNear = 0.80;

      const pts = [];

      for (let iz = 0; iz < NZ; iz++) {
        for (let ix = 0; ix < NX; ix++) {
          const wx = (ix / (NX - 1) - 0.5) * terrW;
          const wz = zNear + (iz / (NZ - 1)) * terrD;
          const wy = fbm(wx * 0.72, wz * 0.58); // height in ~[-1, +1]

          const relZ  = wz + camZOffset;
          const scale = fovY / relZ;
          const sx    = centerX + wx * scale;
          const sy    = horizonY + (camHeight - wy) * scale * 0.48;

          const depthN  = iz / (NZ - 1);
          const heightN = (wy + 1.0) / 2.0; // normalise to [0, 1]

          pts.push({ sx, sy, scale, depthN, heightN, relZ });
        }
      }

      pts.sort((a, b) => b.relZ - a.relZ);

      const scaleNear = fovY / (zNear + camZOffset);

      for (const p of pts) {
        if (p.sx < -8 || p.sx > W + 8 || p.sy < -8 || p.sy > H + 8) continue;

        const size = Math.max(0.35, (p.scale / scaleNear) * 3.6);

        /* Brightness — mix depth (proximity) and height; keep it gentle */
        const bright = (1 - p.depthN) * 0.52 + p.heightN * 0.48;

        /* ── Very muted colour ramp — all tones stay in the dark-green family ── */
        let r, g, b, a;
        if (bright < 0.25) {
          r = 22; g = 36; b = 20;
          a = 0.05 + bright * 0.22;
        } else if (bright < 0.55) {
          const t = (bright - 0.25) / 0.30;
          r = Math.round(22 + t * (110 - 22));
          g = Math.round(36 + t * (138 - 36));
          b = Math.round(20 + t * (105 - 20));
          a = 0.09 + bright * 0.22;
        } else if (bright < 0.80) {
          const t = (bright - 0.55) / 0.25;
          r = Math.round(110 + t * (160 - 110));
          g = Math.round(138 + t * (188 - 138));
          b = Math.round(105 + t * (152 - 105));
          a = 0.16 + bright * 0.22;
        } else {
          /* Peak — pale sage, barely brighter than mid */
          const t = (bright - 0.80) / 0.20;
          r = Math.round(160 + t * (185 - 160));
          g = Math.round(188 + t * (210 - 188));
          b = Math.round(152 + t * (178 - 152));
          a = 0.28 + bright * 0.10;
        }

        ctx.beginPath();
        ctx.arc(p.sx, p.sy, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${Math.min(0.42, a)})`;
        ctx.fill();
      }
    }

    draw();

    const ro = new ResizeObserver(() => draw());
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  return <canvas ref={canvasRef} className={className} style={style} />;
}
